import { Input as CoreInput } from '@vox/frontend-react-ui';
import { createRHFField } from '@vox/frontend-react-ui/rhf';
import { Checkbox } from '@/shared/ui/Checkbox/Checkbox';
import { Calendar, type CalendarProps } from 'primereact/calendar';
import type { ComponentType } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { MaskedInput } from '@vox/frontend-react-ui/input-mask';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import {
    type FieldPathByValue,
    type FieldValues,
} from 'react-hook-form';
import { russianLocale } from '@/core/locales/ru';
import { formatVersion } from '@/shared/lib/formatVersion';
import { formatTime24, parseCalendarDateTime, parseTime24 } from '@/shared/lib/dateTime';
import { DateTimeInput } from '@/shared/ui/DateTimeInput/DateTimeInput';
import classes from './RHFFields.module.scss';

interface FieldBaseProps<TName extends string> {
    name: TName;
    label: string;
    disabled?: boolean;
    isRequired?: boolean;
}

interface TextFieldProps<TFieldValues extends FieldValues>
    extends FieldBaseProps<FieldPathByValue<TFieldValues, string | null | undefined>> {
    placeholder?: string;
    format?: 'version' | 'uppercase';
    type?: 'text' | 'password' | 'email';
    maxLength?: number;
    autoComplete?: string;
}

interface PhoneFieldProps<TFieldValues extends FieldValues>
    extends FieldBaseProps<FieldPathByValue<TFieldValues, string | null | undefined>> {
    placeholder?: string;
    mask?: string;
}

interface TimeFieldProps<TFieldValues extends FieldValues>
    extends FieldBaseProps<FieldPathByValue<TFieldValues, string | null | undefined>> {
    placeholder?: string;
    stepMinute?: number;

    /** Самое раннее время в формате ЧЧ:мм */
    minTime?: string;
}

interface NumberFieldProps<TFieldValues extends FieldValues>
    extends FieldBaseProps<FieldPathByValue<TFieldValues, number | null | undefined>> {
    placeholder?: string;
    min?: number;
    max?: number;
    mode?: 'currency' | 'decimal';
    currency?: string;
    locale?: string;
    minFractionDigits?: number;
    maxFractionDigits?: number;
}

interface DateFieldProps<TFieldValues extends FieldValues>
    extends FieldBaseProps<FieldPathByValue<TFieldValues, Date | null>> {
    placeholder?: string;
    maxDate?: Date;
    minDate?: Date;
    showTime?: boolean;
}

interface TextareaFieldProps<TFieldValues extends FieldValues>
    extends FieldBaseProps<FieldPathByValue<TFieldValues, string | null | undefined>> {
    placeholder?: string;
    rows?: number;
    autoResize?: boolean;
    maxLength?: number;
}

type BooleanFieldProps<TFieldValues extends FieldValues> =
    FieldBaseProps<FieldPathByValue<TFieldValues, boolean | undefined>>;

interface SelectFieldProps<TFieldValues extends FieldValues>
    extends FieldBaseProps<FieldPathByValue<TFieldValues, string | number | null | undefined>> {
    options: Array<{ id: string | number | null; name: string }>;
    placeholder?: string;
    onValueChange?: (value: string | number | null) => void;
}

/** Отображает сообщение об ошибке поля. */
const ErrorMessage = ({ id, message }: { id: string; message?: string }) =>
    message
        ? <small
            id={id}
            className={classes.errorMessage}
            role="alert"
        >
            {message}
        </small>
        : null;

// В PrimeReact 10.9.9 конструктор Calendar теряет TValue, хотя keepInvalid поддерживает строки.
const EditableCalendar = Calendar as ComponentType<CalendarProps<'single', Date | string>>;

/** Сохраняет дату или незавершённый ввод календаря */
const asCalendarValue = (value: unknown): Date | string | null => value instanceof Date || typeof value === 'string'
    ? value
    : null;

/** Создаёт набор полей формы. */
export const createRHFFields = <TFieldValues extends FieldValues>(idPrefix?: string) => {
    const Field = createRHFField<TFieldValues>(idPrefix);

    /** Рендерит текстовое поле формы. */
    const TextField = (
        {
            name,
            label,
            placeholder,
            disabled,
            isRequired,
            format,
            type = 'text',
            maxLength,
            autoComplete,
        }: TextFieldProps<TFieldValues>
    ) => {
        return (
            <Field
                name={name}
                render={({ field, fieldState, fieldId }) => (
                    <CoreInput
                        id={fieldId}
                        name={field.name}
                        ref={field.ref}
                        label={label}
                        type={type}
                        autoComplete={autoComplete ?? (type === 'password'
                            ? 'new-password'
                            : undefined
                        )}
                        value={typeof field.value === 'string'
                            ? field.value
                            : ''
                        }
                        onChange={(event) => field.onChange(
                            format === 'version'
                                ? formatVersion(event.target.value)
                                : format === 'uppercase'
                                    ? event.target.value.toUpperCase()
                                    : event,
                        )}
                        onBlur={field.onBlur}
                        placeholder={placeholder}
                        maxLength={maxLength}
                        disabled={disabled}
                        aria-required={isRequired}
                        aria-invalid={fieldState.invalid}
                        error={fieldState.error?.message}
                        className={classes.control}
                        classNames={{
                            root: classes.field,
                            label: isRequired
                                ? classes.requiredLabel
                                : classes.label,
                            error: classes.errorMessage
                        }}
                        renderInput={(inputProps) => <InputText
                            {...inputProps}
                            value={typeof field.value === 'string'
                                ? field.value
                                : ''
                            }
                            invalid={fieldState.invalid}
                        />}
                    />
                )}
            />
        );
    };

    /** Рендерит числовое поле формы. */
    const NumberField = (
        {
            name,
            label,
            placeholder,
            disabled,
            isRequired,
            min,
            max,
            mode,
            currency,
            locale,
            minFractionDigits,
            maxFractionDigits,
        }: NumberFieldProps<TFieldValues>
    ) => {
        return (
            <Field
                name={name}
                render={({ field, fieldState, fieldId, errorId }) => (
                    <div className={classes.field}>
                        <label
                            htmlFor={fieldId}
                            className={isRequired
                                ? classes.requiredLabel
                                : classes.label
                            }
                        >
                            {label}
                        </label>
                        <InputNumber
                            inputId={fieldId}
                            name={field.name}
                            inputRef={field.ref}
                            value={typeof field.value === 'number'
                                ? field.value
                                : null
                            }
                            onValueChange={(event) => field.onChange(event.value ?? null)}
                            onChange={(event) => field.onChange(event.value ?? null)}
                            onBlur={field.onBlur}
                            placeholder={placeholder}
                            disabled={disabled}
                            min={min}
                            max={max}
                            mode={mode}
                            currency={currency}
                            locale={locale}
                            minFractionDigits={minFractionDigits}
                            maxFractionDigits={maxFractionDigits}
                            useGrouping={false}
                            invalid={fieldState.invalid}
                            aria-required={isRequired}
                            aria-invalid={fieldState.invalid}
                            aria-describedby={fieldState.error
                                ? errorId
                                : undefined
                            }
                            className={classes.control}
                        />
                        <ErrorMessage
                            id={errorId}
                            message={fieldState.error?.message}
                        />
                    </div>
                )}
            />
        );
    };

    /** Отображает поле даты и времени формы */
    const DateField = (
        { name, label, placeholder, disabled, isRequired, minDate, maxDate, showTime = false }: DateFieldProps<TFieldValues>
    ) => {
        return (
            <Field
                name={name}
                render={({ field, fieldState, fieldId, errorId }) => (
                    <div className={classes.field}>
                        <label
                            htmlFor={fieldId}
                            className={isRequired
                                ? classes.requiredLabel
                                : classes.label
                            }
                        >
                            {label}
                        </label>
                        <EditableCalendar
                            locale="ru"
                            inputId={fieldId}
                            name={field.name}
                            inputRef={field.ref}
                            value={asCalendarValue(field.value)}
                            onChange={(event) => {
                                const value: unknown = event.value;

                                field.onChange(value === ''
                                    ? null
                                    : value ?? null);
                            }}
                            onInput={event => {
                                const input = event.currentTarget;
                                const start = input.selectionStart;
                                const end = input.selectionEnd;

                                queueMicrotask(() => {
                                    if (input.isConnected && input === document.activeElement && start !== null && end !== null) {
                                        input.setSelectionRange(start, end);
                                    }
                                });
                            }}
                            onBlur={field.onBlur}
                            dateFormat="dd.mm.yy"
                            placeholder={placeholder ?? (showTime
                                ? 'дд.мм.гггг чч:мм'
                                : 'дд.мм.гггг'
                            )}
                            showTime={showTime}
                            showOnFocus={false}
                            parseDateTime={showTime
                                ? value => {
                                    const date = parseCalendarDateTime(value);

                                    if (!date) throw new Error('Укажите дату и время полностью');

                                    return date;
                                }
                                : undefined
                            }
                            keepInvalid
                            hourFormat="24"
                            minDate={minDate}
                            maxDate={maxDate}
                            showIcon
                            disabled={disabled}
                            invalid={fieldState.invalid}
                            pt={{
                                input: {
                                    root: {
                                        inputMode: 'text',
                                        'aria-required': isRequired,
                                        'aria-invalid': fieldState.invalid,
                                        'aria-describedby': fieldState.error
                                            ? errorId
                                            : undefined,
                                    },
                                }
                            }}
                            className={classes.control}
                        />
                        <ErrorMessage
                            id={errorId}
                            message={fieldState.error?.message}
                        />
                    </div>
                )}
            />
        );
    };

    /** Отображает поле номера телефона. */
    const PhoneField = (
        {
            name,
            label,
            placeholder = 'Введите номер телефона',
            mask = '+7(999) 999-99-99',
            disabled,
            isRequired,
        }: PhoneFieldProps<TFieldValues>
    ) => {
        return (
            <Field
                name={name}
                render={({ field, fieldState, fieldId, errorId }) => (
                    <div className={classes.field}>
                        <label
                            htmlFor={fieldId}
                            className={isRequired
                                ? classes.requiredLabel
                                : classes.label
                            }
                        >
                            {label}
                        </label>
                        <MaskedInput
                            id={fieldId}
                            name={field.name}
                            ref={field.ref}
                            value={typeof field.value === 'string'
                                ? field.value
                                : ''
                            }
                            onValueChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder={placeholder}
                            mask={mask}
                            unstyled={false}
                            autoClear={false}
                            disabled={disabled}
                            aria-required={isRequired}
                            aria-invalid={fieldState.invalid}
                            aria-describedby={fieldState.error
                                ? errorId
                                : undefined
                            }
                            className={classes.control}
                        />
                        <ErrorMessage
                            id={errorId}
                            message={fieldState.error?.message}
                        />
                    </div>
                )}
            />
        );
    };

    /** Рендерит многострочное поле формы. */
    const TextareaField = (
        {
            name,
            label,
            placeholder,
            disabled,
            isRequired,
            rows = 4,
            autoResize = true,
            maxLength,
        }: TextareaFieldProps<TFieldValues>
    ) => {
        return (
            <Field
                name={name}
                render={({ field, fieldState, fieldId, errorId }) => (
                    <div className={classes.field}>
                        <label
                            htmlFor={fieldId}
                            className={isRequired
                                ? classes.requiredLabel
                                : classes.label
                            }
                        >
                            {label}
                        </label>
                        <InputTextarea
                            id={fieldId}
                            name={field.name}
                            ref={field.ref}
                            value={typeof field.value === 'string'
                                ? field.value
                                : ''
                            }
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder={placeholder}
                            disabled={disabled}
                            rows={rows}
                            autoResize={autoResize}
                            maxLength={maxLength}
                            invalid={fieldState.invalid}
                            aria-required={isRequired}
                            aria-invalid={fieldState.invalid}
                            aria-describedby={fieldState.error
                                ? errorId
                                : undefined
                            }
                            className={classes.control}
                        />
                        <ErrorMessage
                            id={errorId}
                            message={fieldState.error?.message}
                        />
                    </div>
                )}
            />
        );
    };

    /** Рендерит поле с флажком. */
    const CheckboxField = ({ name, label, disabled }: BooleanFieldProps<TFieldValues>) => {
        return (
            <Field
                name={name}
                render={({ field, fieldState, fieldId, errorId }) => (
                    <div className={classes.field}>
                        <div className={classes.checkboxRow}>
                            <Checkbox
                                id={fieldId}
                                name={field.name}
                                ref={field.ref}
                                checked={Boolean(field.value)}
                                onCheckedChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={disabled}
                                invalid={fieldState.invalid}
                                aria-invalid={fieldState.invalid}
                                aria-describedby={fieldState.error
                                    ? errorId
                                    : undefined
                                }
                            />
                            <label
                                htmlFor={fieldId}
                                className={classes.label}
                            >
                                {label}
                            </label>
                        </div>
                        <ErrorMessage
                            id={errorId}
                            message={fieldState.error?.message}
                        />
                    </div>
                )}
            />
        );
    };

    /** Рендерит переключатель логического значения. */
    const ToggleSwitchField = ({ name, label, disabled }: BooleanFieldProps<TFieldValues>) => {
        return (
            <Field
                name={name}
                render={({ field, fieldState, fieldId, errorId }) => (
                    <div className={classes.field}>
                        <div className={classes.checkboxRow}>
                            <InputSwitch
                                inputId={fieldId}
                                name={field.name}
                                inputRef={field.ref}
                                checked={Boolean(field.value)}
                                onChange={(event) => field.onChange(event.value)}
                                onBlur={field.onBlur}
                                disabled={disabled}
                                invalid={fieldState.invalid}
                                aria-invalid={fieldState.invalid}
                                aria-describedby={fieldState.error
                                    ? errorId
                                    : undefined
                                }
                                pt={{
                                    root: {
                                        role: 'presentation',
                                        'aria-checked': undefined
                                    }
                                }}
                            />
                            <label
                                htmlFor={fieldId}
                                className={classes.label}
                            >
                                {label}
                            </label>
                        </div>
                        <ErrorMessage
                            id={errorId}
                            message={fieldState.error?.message}
                        />
                    </div>
                )}
            />
        );
    };

    /** Рендерит поле выбора. */
    const SelectField = (
        { name, label, options, placeholder, disabled, isRequired, onValueChange }: SelectFieldProps<TFieldValues>
    ) => {
        return (
            <Field
                name={name}
                render={({ field, fieldState, fieldId, errorId }) => (
                    <div className={classes.field}>
                        <label
                            htmlFor={fieldId}
                            className={isRequired
                                ? classes.requiredLabel
                                : classes.label
                            }
                        >
                            {label}
                        </label>
                        <Dropdown
                            inputId={fieldId}
                            name={field.name}
                            inputRef={field.ref}
                            ariaLabel={label}
                            value={typeof field.value === 'string' || typeof field.value === 'number'
                                ? field.value
                                : null
                            }
                            onChange={(event) => {
                                field.onChange(event.value);
                                onValueChange?.(event.value);
                            }}
                            onBlur={field.onBlur}
                            options={options}
                            optionLabel="name"
                            optionValue="id"
                            emptyMessage={russianLocale.emptyMessage}
                            emptyFilterMessage={russianLocale.emptyFilterMessage}
                            placeholder={placeholder ?? 'Выберите значение'}
                            disabled={disabled}
                            invalid={fieldState.invalid}
                            aria-required={isRequired}
                            aria-invalid={fieldState.invalid}
                            aria-describedby={fieldState.error
                                ? errorId
                                : undefined
                            }
                            pt={{
                                root: { 'aria-activedescendant': undefined },
                                select: { 'aria-label': label },
                                trigger: { 'aria-label': placeholder ?? label },
                            }}
                            className={classes.control}
                        />
                        <ErrorMessage
                            id={errorId}
                            message={fieldState.error?.message}
                        />
                    </div>
                )}
            />
        );
    };

    /** Позволяет выбрать время суток */
    const TimeField = (
        { name, label, placeholder, disabled, isRequired, stepMinute = 1, minTime }: TimeFieldProps<TFieldValues>
    ) => {
        return (
            <Field
                name={name}
                render={({ field, fieldState }) => (
                    <DateTimeInput
                        name={field.name}
                        label={label}
                        mode="time"
                        value={parseTime24(field.value)}
                        onChange={(event) => field.onChange(event.value
                            ? formatTime24(event.value.getTime())
                            : '')}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        error={fieldState.error?.message}
                        disabled={disabled}
                        isRequired={isRequired}
                        placeholder={placeholder}
                        stepMinute={stepMinute}
                        minDate={parseTime24(minTime) ?? undefined}
                    />
                )}
            />
        );
    };

    return {
        TextField,
        PhoneField,
        NumberField,
        DateField,
        TimeField,
        TextareaField,
        CheckboxField,
        ToggleSwitchField,
        SelectField
    };
};
